import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { QRCodeCanvas } from 'qrcode.react';
import Hls from 'hls.js';
import { Toaster, toast } from 'sonner';
import { Radio, Loader2, Tv2, CheckCircle2, AlertCircle } from 'lucide-react';
import { liveApi, setViewerToken, setViewerMeta } from './liveClient';

/**
 * Smart-TV QR login flow:
 *  - TV calls POST /livestream/qr/init -> { qr_id, qr_token }
 *  - TV displays QR code containing the mobile URL /live/login?claim=<qr_token>
 *  - User scans on mobile, logs in (or is already logged in), and the mobile triggers POST /livestream/qr/claim/<qr_token>
 *  - TV polls GET /livestream/qr/poll/<qr_id> every 2s; once claimed, TV receives a viewer token and auto-loads the user's streams
 */
export default function LiveTvLoginPage() {
  const nav = useNavigate();
  const [qr, setQr] = useState(null);
  const [status, setStatus] = useState('init');
  const [streams, setStreams] = useState([]);
  const pollRef = useRef(null);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const claimUrl = qr ? `${window.location.origin}/live/login?claim=${qr.qr_token}` : '';

  const initQr = async () => {
    setStatus('init');
    try {
      const r = await liveApi().post('/livestream/qr/init');
      setQr(r.data); setStatus('pending');
    } catch { setStatus('error'); }
  };
  useEffect(() => { initQr(); }, []);

  // Poll
  useEffect(() => {
    if (status !== 'pending' || !qr?.qr_id) return;
    pollRef.current = setInterval(async () => {
      try {
        const r = await liveApi().get(`/livestream/qr/poll/${qr.qr_id}`);
        if (r.data.status === 'claimed') {
          clearInterval(pollRef.current);
          setViewerToken(r.data.token);
          setViewerMeta(r.data.viewer);
          setStatus('claimed');
          // Auto-load streams
          try {
            const me = await liveApi().get('/livestream/viewer/my-streams');
            const list = me.data?.streams || [];
            setStreams(list);
            // If exactly one live stream, auto-play it
            const live = list.find((s) => s.status === 'live');
            if (live) startWatching(live);
          } catch { /* ignore */ }
        } else if (r.data.status === 'expired') {
          clearInterval(pollRef.current);
          setStatus('expired');
        }
      } catch { /* network blip; keep trying */ }
    }, 2000);
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, [status, qr?.qr_id]);

  const startWatching = async (s) => {
    try {
      const r = await liveApi().get(`/livestream/viewer/watch/${s.id}`);
      const url = r.data.stream?.playback_url;
      if (!url) { toast.error('No playback URL'); return; }
      const v = videoRef.current;
      if (Hls.isSupported()) {
        const hls = new Hls({ lowLatencyMode: true, liveDurationInfinity: true });
        hlsRef.current = hls; hls.loadSource(url); hls.attachMedia(v);
      } else if (v.canPlayType('application/vnd.apple.mpegurl')) {
        v.src = url;
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  // Heartbeat while watching/claimed
  useEffect(() => {
    if (status !== 'claimed') return;
    const t = setInterval(async () => {
      try { await liveApi().post('/livestream/viewer/heartbeat'); }
      catch (e) {
        if (e?._kicked) { setStatus('kicked'); clearInterval(t); }
      }
    }, 30000);
    return () => clearInterval(t);
  }, [status]);

  useEffect(() => () => { if (hlsRef.current) hlsRef.current.destroy(); }, []);

  return (
    <div className="min-h-screen bg-black text-white">
      <Toaster richColors position="top-center" />
      <header className="px-6 py-4 flex items-center gap-3 border-b border-slate-800">
        <Radio className="w-6 h-6 text-rose-400" />
        <h1 className="text-xl font-bold">SMNGP Live — TV</h1>
      </header>

      {status === 'init' && (
        <div className="flex flex-col items-center justify-center py-32"><Loader2 className="w-10 h-10 animate-spin text-rose-400" /><div className="mt-4 text-slate-400">Preparing TV pairing…</div></div>
      )}

      {(status === 'pending' || status === 'expired') && qr && (
        <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
          <Tv2 className="w-16 h-16 text-rose-400 mb-4" />
          <h2 className="text-3xl font-bold mb-1">Scan to sign in on this TV</h2>
          <p className="text-slate-400 mb-6 max-w-xl">Open your phone camera and scan the QR. Sign in with your ITS. Your TV will start streaming automatically.</p>
          <div className="bg-white p-6 rounded-2xl">
            <QRCodeCanvas value={claimUrl} size={320} bgColor="#ffffff" fgColor="#000000" level="M" />
          </div>
          <div className="mt-6 text-slate-300 text-sm">
            Or visit: <code className="text-rose-300">{claimUrl.replace('https://', '').replace('http://', '')}</code>
          </div>
          {status === 'expired' && (
            <div className="mt-6 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-400" />
              <span className="text-amber-300">Code expired — </span>
              <button onClick={initQr} className="underline text-rose-300">refresh</button>
            </div>
          )}
        </div>
      )}

      {status === 'claimed' && (
        <div className="p-4">
          <div className="flex items-center gap-2 text-emerald-400 mb-3"><CheckCircle2 className="w-5 h-5" />Signed in. Streams loaded.</div>
          {streams.length === 0 ? (
            <div className="bg-slate-900 border border-slate-800 rounded-lg p-10 text-center">
              <Tv2 className="w-12 h-12 text-slate-500 mx-auto mb-3" />
              <h3 className="font-semibold text-lg">No streams assigned</h3>
              <p className="text-slate-400 text-sm mt-1">You haven't been granted access to any stream yet.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2 aspect-video bg-slate-950 rounded-lg overflow-hidden border border-slate-800">
                <video ref={videoRef} controls autoPlay playsInline className="w-full h-full" data-testid="tv-video" />
              </div>
              {streams.map((s) => (
                <button key={s.id} onClick={() => startWatching(s)} className="bg-slate-900 border border-slate-800 rounded-lg p-4 text-left hover:border-rose-500 transition-colors">
                  <div className="font-bold text-lg">{s.title}</div>
                  <div className="text-xs text-slate-400 mt-1">{s.status === 'live' ? '🔴 LIVE' : s.status.toUpperCase()} · {s.category}</div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {status === 'kicked' && (
        <div className="flex flex-col items-center justify-center py-24">
          <AlertCircle className="w-12 h-12 text-amber-400 mb-3" />
          <div className="text-amber-200">Your account has been logged in from another device. This session has been closed.</div>
          <button onClick={() => { setStatus('init'); initQr(); }} className="mt-4 underline text-rose-300">Pair this TV again</button>
        </div>
      )}
    </div>
  );
}
